/**
 * Exchange Rules API Service - Unified API
 *
 * Service for managing all Exchange Online rules (transport, DLP, retention, etc.)
 * via the unified backend API with type dispatch pattern. Replaces dedicated
 * transport rules endpoints with single unified Exchange API.
 *
 * Follows zero-mocking testing strategy with real API calls to running backend.
 */

import { ApiService } from './api';
import type {
    ExchangeRuleRequest,
    ExchangeRuleResponse,
    ExchangeRuleUpdateRequest,
    ExchangeRuleListResponse,
    TransportRule,
    CreateRulePriorityRequest,
    CreateClientDomainRuleRequest,
    UpdatePriorityRequest
} from '../types/transportRules';

// ============================================================================
// UNIFIED EXCHANGE RULES API (NEW)
// ============================================================================

/**
 * Unified Exchange Rules API Service
 * Provides 5 unified endpoints with type dispatch pattern
 */
export class ExchangeRulesApiService {
    /**
     * Create an Exchange rule via type dispatch
     *
     * @param tenantId - Azure AD tenant ID
     * @param request - Rule creation request with type dispatch
     * @returns Created rule response
     *
     * @example
     * ```ts
     * const rule = await ExchangeRulesApiService.createRule(tenantId, {
     *   type: 'transport',
     *   rule_type: 'block-external-senders',
     *   priority: 0,
     *   enabled: true
     * });
     * ```
     */
    static async createRule(
        tenantId: string,
        request: ExchangeRuleRequest
    ): Promise<ExchangeRuleResponse> {
        return ApiService.request<ExchangeRuleResponse>(
            `/api/tenants/${tenantId}/exchange/rules`,
            {
                method: 'POST',
                body: JSON.stringify(request)
            }
        );
    }

    /**
     * List all Exchange rules with optional type filter
     *
     * @param tenantId - Azure AD tenant ID
     * @param ruleType - Optional rule type filter (e.g., 'transport')
     * @returns List response with rules array and total count
     *
     * @example
     * ```ts
     * const response = await ExchangeRulesApiService.listRules(tenantId);
     * console.log(response.rules);  // ExchangeRuleResponse[]
     * console.log(response.total);  // number
     * ```
     */
    static async listRules(
        tenantId: string,
        ruleType?: string
    ): Promise<ExchangeRuleListResponse> {
        const query = ruleType ? `?rule_type=${ruleType}` : '';
        return ApiService.request<ExchangeRuleListResponse>(
            `/api/tenants/${tenantId}/exchange/rules${query}`,
            { method: 'GET' }
        );
    }

    /**
     * Get a single Exchange rule by identity
     *
     * @param tenantId - Azure AD tenant ID
     * @param ruleId - Rule identity/ID from Exchange Online
     * @returns Single rule response
     */
    static async getRule(
        tenantId: string,
        ruleId: string
    ): Promise<ExchangeRuleResponse> {
        return ApiService.request<ExchangeRuleResponse>(
            `/api/tenants/${tenantId}/exchange/rules/${ruleId}`,
            { method: 'GET' }
        );
    }

    /**
     * Update an Exchange rule via PATCH
     * Supports updating name, priority, and enabled state
     *
     * @param tenantId - Azure AD tenant ID
     * @param ruleId - Rule identity/ID from Exchange Online
     * @param request - Update request with optional fields
     *
     * @example
     * ```ts
     * // Update priority only
     * await ExchangeRulesApiService.updateRule(tenantId, ruleId, { priority: 5 });
     *
     * // Enable rule
     * await ExchangeRulesApiService.updateRule(tenantId, ruleId, { enabled: true });
     *
     * // Update multiple fields
     * await ExchangeRulesApiService.updateRule(tenantId, ruleId, {
     *   name: 'Updated Name',
     *   priority: 3,
     *   enabled: false
     * });
     * ```
     */
    static async updateRule(
        tenantId: string,
        ruleId: string,
        request: ExchangeRuleUpdateRequest
    ): Promise<void> {
        await ApiService.request<void>(
            `/api/tenants/${tenantId}/exchange/rules/${ruleId}`,
            {
                method: 'PATCH',
                body: JSON.stringify(request)
            }
        );
    }

    /**
     * Delete an Exchange rule
     *
     * @param tenantId - Azure AD tenant ID
     * @param ruleId - Rule identity/ID from Exchange Online
     */
    static async deleteRule(tenantId: string, ruleId: string): Promise<void> {
        await ApiService.request<void>(
            `/api/tenants/${tenantId}/exchange/rules/${ruleId}`,
            { method: 'DELETE' }
        );
    }
}

// ============================================================================
// BACKWARD COMPATIBILITY API (DEPRECATED)
// ============================================================================

/**
 * @deprecated Use ExchangeRulesApiService instead
 * Legacy Transport Rules API Service - kept for backward compatibility during migration
 */
export class TransportRulesApiService {
    /**
     * @deprecated Use ExchangeRulesApiService.listRules() instead
     * List all transport rules for a tenant
     */
    static async listRules(tenantId: string): Promise<TransportRule[]> {
        const response = await ExchangeRulesApiService.listRules(tenantId, 'transport');
        return response.rules;
    }

    /**
     * @deprecated Use ExchangeRulesApiService.getRule() instead
     * Get a single transport rule by ID
     */
    static async getRule(tenantId: string, ruleId: string): Promise<TransportRule> {
        return ExchangeRulesApiService.getRule(tenantId, ruleId);
    }

    /**
     * @deprecated Use ExchangeRulesApiService.createRule() instead
     * Create a "Block External Senders" rule
     */
    static async createBlockExternalSendersRule(
        tenantId: string,
        request: CreateRulePriorityRequest
    ): Promise<TransportRule> {
        return ExchangeRulesApiService.createRule(tenantId, {
            type: 'transport',
            rule_type: 'block-external-senders',
            priority: request.priority,
            enabled: request.enabled
        });
    }

    /**
     * @deprecated Use ExchangeRulesApiService.createRule() instead
     * Create a "Block Auto-Forward to External Recipients" rule
     */
    static async createBlockAutoForwardRule(
        tenantId: string,
        request: CreateRulePriorityRequest
    ): Promise<TransportRule> {
        return ExchangeRulesApiService.createRule(tenantId, {
            type: 'transport',
            rule_type: 'block-auto-forward',
            priority: request.priority,
            enabled: request.enabled
        });
    }

    /**
     * @deprecated Use ExchangeRulesApiService.createRule() instead
     * Create a "Block Client Domain Spoofing" rule
     */
    static async createBlockClientDomainRule(
        tenantId: string,
        request: CreateClientDomainRuleRequest
    ): Promise<TransportRule> {
        return ExchangeRulesApiService.createRule(tenantId, {
            type: 'transport',
            rule_type: 'block-client-domain',
            priority: request.priority,
            enabled: request.enabled,
            configuration: {
                client_domain: request.client_domain
            }
        });
    }

    /**
     * @deprecated Use ExchangeRulesApiService.updateRule() instead
     * Enable a transport rule
     */
    static async enableRule(tenantId: string, ruleId: string): Promise<TransportRule> {
        await ExchangeRulesApiService.updateRule(tenantId, ruleId, { enabled: true });
        return ExchangeRulesApiService.getRule(tenantId, ruleId);
    }

    /**
     * @deprecated Use ExchangeRulesApiService.updateRule() instead
     * Disable a transport rule
     */
    static async disableRule(tenantId: string, ruleId: string): Promise<TransportRule> {
        await ExchangeRulesApiService.updateRule(tenantId, ruleId, { enabled: false });
        return ExchangeRulesApiService.getRule(tenantId, ruleId);
    }

    /**
     * @deprecated Use ExchangeRulesApiService.updateRule() instead
     * Update a transport rule's priority
     */
    static async updatePriority(
        tenantId: string,
        ruleId: string,
        request: UpdatePriorityRequest
    ): Promise<TransportRule> {
        await ExchangeRulesApiService.updateRule(tenantId, ruleId, { priority: request.priority });
        return ExchangeRulesApiService.getRule(tenantId, ruleId);
    }

    /**
     * @deprecated Use ExchangeRulesApiService.deleteRule() instead
     * Delete a transport rule
     */
    static async deleteRule(tenantId: string, ruleId: string): Promise<void> {
        await ExchangeRulesApiService.deleteRule(tenantId, ruleId);
    }
}
